import type { JsonObject } from "../shared/json";

/**
 * One vendor variant of a listing (a StockX size, say). The four named fields
 * are what code computes on or shows; `details` is the vendor's own payload and
 * no backend code branches on its contents.
 */
export interface ListingVariant {
  /** The vendor's own variant id, never ours. */
  variantId: string;
  /** What the vendor calls this variant ("3.5", "PSA 10"); null when it names none. */
  variantValue: string | null;
  /** Acquisition cost in USD cents, as the vendor quotes it. No markup applied. */
  price: number;
  /** ISO-8601 instant the vendor's quote was read. */
  updatedAt: string;
  /** Vendor-native detail, stored opaque. */
  details: JsonObject;
}

export type ListingVendor = "STOCKX" | "FANATICS" | "MANUAL" | "IN_HOUSE" | "OTHER";

/**
 * Whether a listing can be shown and browsed; only ACTIVE can. IMAGE_PROCESSING keeps a listing
 * hidden until its processed image is stored.
 */
export type ListingStatus = "ACTIVE" | "INACTIVE" | "IMAGE_PROCESSING";

/**
 * The statuses a tenant may send or receive. A tenant never sees IMAGE_PROCESSING: such a row
 * reports as INACTIVE.
 */
export type TenantListingStatus = Exclude<ListingStatus, "IMAGE_PROCESSING">;

/** Every listing vendor and status, in one list each so validators derive from the types. */
export const LISTING_VENDORS = [
  "STOCKX",
  "FANATICS",
  "MANUAL",
  "IN_HOUSE",
  "OTHER",
] as const satisfies readonly ListingVendor[];

export const LISTING_STATUSES = [
  "ACTIVE",
  "INACTIVE",
  "IMAGE_PROCESSING",
] as const satisfies readonly ListingStatus[];

/** Every status a tenant may send or receive, so tenant validators derive from the type. */
export const TENANT_LISTING_STATUSES = [
  "ACTIVE",
  "INACTIVE",
] as const satisfies readonly TenantListingStatus[];

/**
 * The two kinds of StockX listing a feature flag shows or hides on the tenant browse routes. A
 * StockX row is SNEAKERS when its category is `sneakers` and TCG otherwise (`collectibles`, or no
 * category), so every StockX row is one of the two.
 */
export enum StockxListingKind {
  SNEAKERS = "SNEAKERS",
  TCG = "TCG",
}

/**
 * Taxonomy tags applied to listings for browse/markup (boxed MarketplaceCategory).
 *
 * SNEAKERS (ENG-7176) tags StockX sneaker listings. Two decisions go with it:
 * - It is not in REDUCED_MARKUP_CATEGORIES, so it takes the default 1.25x. The reduced band was
 *   fitted to trading-card prices, and a StockX sneaker's stored price already carries StockX's
 *   fee and tax (ENG-6985). Revisit after the first month of sneaker sales.
 * - findCategoriesInString (listing/ingest.ts) has no sneakers case. It maps scraped Fanatics and
 *   TCGplayer text. Every STOCKX writer adds the tag to a product StockX files as a sneaker
 *   (`withStockxCategoryTags` in product-fetch/to-listing.ts), whatever tags the rule or operator
 *   picked, since Boxed reads it to show the size step.
 */
export type MarketplaceCategory =
  | "POKEMON"
  | "MTG"
  | "YUGIOH"
  | "BASEBALL"
  | "BASKETBALL"
  | "FOOTBALL"
  | "HOCKEY"
  | "SOCCER"
  | "RACING"
  | "ONE_PIECE"
  | "SEALED"
  | "COMBAT_SPORTS"
  | "GOLF"
  | "RIFTBOUND"
  | "SNEAKERS";

export const MARKETPLACE_CATEGORIES: MarketplaceCategory[] = [
  "POKEMON",
  "MTG",
  "YUGIOH",
  "BASEBALL",
  "BASKETBALL",
  "FOOTBALL",
  "HOCKEY",
  "SOCCER",
  "RACING",
  "ONE_PIECE",
  "SEALED",
  "COMBAT_SPORTS",
  "GOLF",
  "RIFTBOUND",
  "SNEAKERS",
];

export type MarketplaceLanguage = "ENGLISH" | "JAPANESE" | "OTHER_LANGUAGES";

export const MARKETPLACE_LANGUAGE_TAGS = new Set<string>([
  "ENGLISH",
  "JAPANESE",
  "OTHER_LANGUAGES",
]);

export const GREAT_PRICE_TAG = "GREAT_PRICE";

/** Categories marked up 1.15x instead of 1.25x; the console names them, so they are not private to pricing.ts. */
export const REDUCED_MARKUP_CATEGORIES = new Set<MarketplaceCategory>([
  "POKEMON",
  "MTG",
  "YUGIOH",
  "ONE_PIECE",
]);

/**
 * Whether deactivating the row changes it: it is ACTIVE, or it waits for its processed image
 * (IMAGE_PROCESSING) with stock. The rule `deactivateActiveListingsByIds` matches rows on.
 */
export function isDeactivatable(listing: Pick<Listing, "status" | "availableQty">): boolean {
  if (listing.status === "ACTIVE") return true;
  return listing.status === "IMAGE_PROCESSING" && listing.availableQty > 0;
}

/** Tags are matched exactly, so free-form operator input is folded to the SCREAMING_SNAKE stored tags use. */
export function normalizeListingTag(tag: string): string {
  return tag
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

export interface Listing {
  id: string;
  vendor: ListingVendor;
  title: string;
  image: string | null;
  attributes: Record<string, unknown> | null;
  /** Per-variant acquisition costs; null for vendors that sell a single SKU. */
  variants: ListingVariant[] | null;
  price: number;
  availableQty: number;
  status: ListingStatus;
  tags: string[];
  category: string | null;
  scrapedAt: Date | null;
  updatedAt: Date;
  expiresAt: Date | null;
  tenantId: string | null;
  onMarketplace: boolean;
}

/**
 * A listing as read back from `vendor_listings`, with the columns only the database holds. A
 * writer builds a {@link Listing}; `salesCount` is the order stock path's alone to change.
 */
export interface StoredListing extends Listing {
  /** Units our own marketplace orders hold (`vendor_listings.sales_count`). */
  salesCount: number;
}
